import {useCallback} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSnackbar} from 'packages/common/components/snackbar';
import {useAuthContext} from 'packages/common/hooks/useAuthContext';

/**
 * Hook pour l'inscription des utilisateurs
 *
 * Gère la création d'un nouveau compte utilisateur avec entreprise.
 * Affiche des notifications de succès ou d'erreur selon le résultat.
 *
 * @return {Function} Fonction d'inscription async
 */
export const useRegister = () => {
  const {enqueueSnackbar} = useSnackbar();

  const register = async (email, password, companyName, firstName, lastName) => {
    try {
      const response = await axiosInstance.post(endpoints.auth.register, {
        email, password, companyName, firstName, lastName,
      });
      enqueueSnackbar('Inscription réussie !', {variant: 'success'});
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);

      let errorMessage = 'Erreur inconnue du serveur'; // Message par défaut
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }

      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    }
  };

  return register;
};

/**
 * Hook pour la connexion des utilisateurs
 *
 * Authentifie l'utilisateur via l'API. Le rechargement de la session
 * est géré automatiquement par SessionProvider après le login.
 * Gère les cas de compte désactivé.
 *
 * @return {Function} Fonction de connexion async
 */
export const useLogin = () => {
  const login = async (email, password, deviceId) => {
    try {
      const response = await axiosInstance.post(endpoints.auth.login, {email, password, deviceId});
      return response.data;
    } catch (error) {
      if (error.response) {
        if (error.response.status === 403 && error.response.data.message === 'Compte désactivé. Veuillez contacter l\'administrateur principal.') {
          throw new Error('COMPTE_DESACTIVE');
        }
      }
      throw error;
    }
  };

  return login;
};

/**
 * Hook pour la déconnexion des utilisateurs
 *
 * Gère uniquement le nettoyage asynchrone : appel POST /auth/logout
 * et notification de succès. L'invalidation du cache SWR et le nettoyage
 * synchrone (session store, localStorage, auth state) sont assurés
 * par AuthProvider.logout() pour éviter les race conditions
 * lors d'une re-connexion rapide.
 *
 * @return {Function} Fonction de déconnexion async
 */
export const useLogout = () => {
  const {enqueueSnackbar} = useSnackbar();

  const logoutAction = async () => {
    try {
      await axiosInstance.post(endpoints.auth.logout);
    } catch (error) {
      // Le nettoyage côté serveur peut échouer (token déjà expiré, etc.)
      // On continue car le nettoyage client est déjà fait par AuthProvider
      console.error('[useLogout] POST /auth/logout failed:', error?.message);
    }

    enqueueSnackbar('Déconnexion réussie !', {variant: 'success'});
  };

  return logoutAction;
};

/**
 * Hook de confirmation de la session courante.
 *
 * Confirme auprès du serveur que la session est toujours ouverte et enregistre
 * qui elle désigne. Il ne ferme jamais de session : un refus avéré est traité
 * par l'intercepteur Axios, un échec passager remonte à l'appelant qui décidera
 * de retenter. Lui faire fermer la session reviendrait à déconnecter sur une
 * coupure réseau ou un backend en cours de redémarrage.
 *
 * @return {Function} Fonction de confirmation async
 */
export const useVerifyToken = () => {
  const {setAuthInfo} = useAuthContext();

  const verifyToken = useCallback(async () => {
    const {data} = await axiosInstance.get(endpoints.auth.verifyToken);

    if (!data || !data.user) {
      return;
    }

    setAuthInfo({
      userId: data.user.userId,
      role: data.user.role,
      emailVerified: data.user.emailVerified,
    });
  }, [setAuthInfo]);

  return verifyToken;
};

/**
 * Hook pour demander la réinitialisation du mot de passe
 *
 * Envoie un email avec un lien de réinitialisation de mot de passe.
 * Affiche des notifications de succès ou d'erreur selon le résultat.
 *
 * @return {Function} Fonction pour demander la réinitialisation async
 */
export const useRequestResetPassword = () => {
  const {enqueueSnackbar} = useSnackbar();

  const requestResetPassword = async (email) => {
    try {
      const response = await axiosInstance.post(endpoints.auth.requestResetPassword, {email});
      enqueueSnackbar(response.data.message, {variant: 'success'});
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la demande de réinitialisation du mot de passe:', error);

      const errorMessage = error?.response?.data?.message || 'Erreur inconnue du serveur';
      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    }
  };

  return requestResetPassword;
};

/**
 * Hook pour réinitialiser le mot de passe
 *
 * Réinitialise le mot de passe de l'utilisateur avec le token reçu par email.
 * Affiche des notifications de succès ou d'erreur selon le résultat.
 *
 * @return {Function} Fonction de réinitialisation du mot de passe async
 */
export const useResetPassword = () => {
  const {enqueueSnackbar} = useSnackbar();

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axiosInstance.post(endpoints.auth.resetPassword, {token, newPassword});
      enqueueSnackbar('Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.', {variant: 'success'});
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);

      const errorMessage = error?.response?.data?.message || 'Erreur inconnue du serveur';
      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    }
  };

  return resetPassword;
};

/**
 * Hook pour vérifier la validité du token de réinitialisation
 *
 * Vérifie si le token de réinitialisation de mot de passe est toujours valide.
 * Utilisé pour afficher le formulaire de réinitialisation uniquement si le token est valide.
 *
 * @param {string} token - Token de réinitialisation reçu par email
 * @return {Function} Fonction de vérification du token async
 */
export const useVerifyResetToken = (token) => {
  const {enqueueSnackbar} = useSnackbar();

  const verifyResetToken = async () => {
    try {
      await axiosInstance.get(endpoints.auth.verifyResetToken(token));
      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification du token de réinitialisation:', error);
      const errorMessage = error?.response?.data?.message || 'Erreur inconnue du serveur';
      enqueueSnackbar(errorMessage, {variant: 'error'});
      return false;
    }
  };

  return verifyResetToken;
};
